import React, { useState, useEffect } from "react";
import {
    Menu,
    MenuItem,
    IconButton,
    Typography,
    Divider,
    Box,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoIcon from "@mui/icons-material/Info";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useNavigate } from "react-router-dom";
import { intl, getToken, DmsPermissions, useDmsLogoffTabs, fetchData } from "dmsbasereact";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { Permissions } from "../../constants/Permissions";
import { getUserTypeLabel, mapBackendTypeToFrontend } from "../../constants/UserType";

export const DmsUserInformation = () => {
    const header_text = intl.get("UserInformation").d("User Information");
    const login_text = intl.get("Login").d("Login");
    const name_text = intl.get("Name").d("Name");
    const user_type_text = intl.get("UserType").d("User Type");
    const profile_settings_text = intl.get("ProfileSettings").d("Profile Settings");
    const about_text = intl.get("About").d("About");
    const logout_text = intl.get("Logout").d("Logout");

    const navigate = useNavigate();
    const logoff = useDmsLogoffTabs();
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const token = getToken();
    const user_login = token?.login ?? "-";
    const user_name = token?.name ?? "-";

    const [anchorEl, setAnchorEl] = useState(null);
    const [userTypeValues, setUserTypeValues] = useState([]);

    // Fetch user types from backend - refresh when menu opens
    useEffect(() => {
        const fetchUserType = async () => {
            try {
                const response = await fetchData('/person/my_profile', 'GET', true);
                if (response?.data.data?.user_types) {
                    // Filter out "person" and "regular_user" as they are not meaningful for display
                    // Also filter out "institution_responsible" if "distributor_responsible" is present
                    const types = response.data.data.user_types;
                    const hasDistributor = types.includes("distributor_responsible");

                    const displayableTypes = types
                        .filter(t => t !== "person" && t !== "regular_user")
                        .filter(t => !(hasDistributor && t === "institution_responsible"))
                        .map(t => mapBackendTypeToFrontend(t));
                    setUserTypeValues(displayableTypes);
                }
            } catch (error) {
                // If fails, keep default empty array
            }
        };
        // Fetch on mount and whenever menu opens
        if (anchorEl !== null) {
            fetchUserType();
        }
    }, [anchorEl]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProfileSettings = () => {
        handleClose();
        navigate(APP_ROUTES.ProfileSettings);
    };

    const handleAbout = () => {
        handleClose();
        navigate(APP_ROUTES.About);
    };

    const handleLogout = async () => {
        handleClose();
        // Call backend logout endpoint for GxP audit trail
        try {
            await fetchData('/auth/logout', 'POST', true);
        } catch (error) {
            // Continue with logout even if backend call fails
            console.error('Backend logout error:', error);
        }
        logoff();
    };

    return (
        <Box>
            <Tooltip title={intl.get("Tooltip.Action.OpenMenu").d("Open menu")}>
                <IconButton color="inherit" onClick={handleClick}>
                    <AccountCircleIcon />
                    {!isSmallScreen && (
                        <Typography variant="body1" sx={{ marginLeft: 1 }}>
                            {user_login}
                        </Typography>
                    )}
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{ paper: { sx: { width: 320, mt: 1 } } }}
            >
                {/* Header */}
                <MenuItem disabled>
                    <Typography variant="h6">
                        <strong>{header_text}</strong>
                    </Typography>
                </MenuItem>
                <Divider />

                {/* User Details */}
                <MenuItem disabled>
                    <Typography variant="body2">
                        <label>{name_text}:</label>&nbsp;<span>{user_name}</span>
                    </Typography>
                </MenuItem>
                <MenuItem disabled>
                    <Typography variant="body2">
                        <label>{login_text}:</label>&nbsp;<span>{user_login}</span>
                    </Typography>
                </MenuItem>
                <MenuItem disabled>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user_type_text}:
                        </Typography>
                        {userTypeValues.length > 0 ? (
                            userTypeValues.map((type, index) => (
                                <Typography key={index} variant="body2" sx={{ pl: 1.5, mt: 0.5 }}>
                                    • {getUserTypeLabel(type)}
                                </Typography>
                            ))
                        ) : (
                            <Typography variant="body2" sx={{ pl: 1.5, mt: 0.5 }}>
                                -
                            </Typography>
                        )}
                    </Box>
                </MenuItem>
                <Divider sx={{ my: 1 }} />

                {/* Action Icons - Side by Side */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    alignItems: 'flex-start',
                    px: 1,
                    py: 1.5
                }}>
                    {/* Profile Settings - Only for users who can edit their profile */}
                    <DmsPermissions
                        permissions={[Permissions.PROFILE_EDIT]}
                    >
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            flex: 1,
                            minWidth: 0,
                            px: 0.5,
                            '&:hover': { opacity: 0.7 }
                        }}
                        onClick={handleProfileSettings}
                        >
                            <IconButton size="small" color="primary">
                                <SettingsIcon />
                            </IconButton>
                            <Typography
                                variant="caption"
                                sx={{
                                    mt: 0.5,
                                    fontSize: '0.7rem',
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {profile_settings_text}
                            </Typography>
                        </Box>
                    </DmsPermissions>

                    {/* About */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        flex: 1,
                        minWidth: 0,
                        px: 0.5,
                        '&:hover': { opacity: 0.7 }
                    }}
                    onClick={handleAbout}
                    >
                        <IconButton size="small" color="primary">
                            <InfoIcon />
                        </IconButton>
                        <Typography
                            variant="caption"
                            sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                wordBreak: 'break-word'
                            }}
                        >
                            {about_text}
                        </Typography>
                    </Box>

                    {/* Logout */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        flex: 1,
                        minWidth: 0,
                        px: 0.5,
                        '&:hover': { opacity: 0.7 }
                    }}
                    onClick={handleLogout}
                    >
                        <IconButton size="small" color="primary">
                            <ExitToAppIcon />
                        </IconButton>
                        <Typography
                            variant="caption"
                            sx={{
                                mt: 0.5,
                                fontSize: '0.7rem',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                wordBreak: 'break-word'
                            }}
                        >
                            {logout_text}
                        </Typography>
                    </Box>
                </Box>
            </Menu>
        </Box>
    );
};
